import { useMemo, useState } from 'react';

import IconCaretDown from '@/assets/Icons/IconCaretDown';
import { Button } from '@/components/atoms/Button';
import { useEstimation } from '@/context/estimationContext';
import { useMessages } from '@/hooks/messages';
import { useLocationMatch } from '@/hooks/useLocationMatch';
import { EstimationNavigationItem } from '@/pages/estimation/components/estimationNavigation/estimationNavigationItem';
import {
  getSetupSteps,
  getWizardSteps,
} from '@/pages/estimation/components/estimationNavigation/model';

import EstimationVersionModal from '../../detail/EstimationVersionModal';
import { NavigationMessages } from './messages';

interface IWizardModeNavigationProps {
  isSidebarOpen: boolean;
  onItemClick?: () => void;
}

export const WizardModeNavigation: React.FC<IWizardModeNavigationProps> = ({
  isSidebarOpen,
  onItemClick,
}) => {
  const {
    state: { savedEstimation, phaseResponses },
  } = useEstimation();

  const { hasPartialMatch } = useLocationMatch();

  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);

  const messages = useMessages(NavigationMessages);
  const wizardSteps = useMemo(() => getWizardSteps(messages), [messages]);
  const setupSteps = useMemo(() => getSetupSteps(messages), [messages]);

  const stepOffset = useMemo(() => {
    return setupSteps.reduce((total, step) => total + (step.children?.length || 0), 0);
  }, [setupSteps]);

  const parentIndexOffset = useMemo(() => {
    return setupSteps.length;
  }, [setupSteps]);

  const indexOfPhases = useMemo(
    () => wizardSteps.findIndex((s) => s.link === '/estimation/new/project-phases'),
    [wizardSteps],
  );

  // Show wizard navigation only when user has a saved estimation and is on a wizard step
  const isActive =
    !!savedEstimation?.estimation.id && wizardSteps.some((s) => s.link && hasPartialMatch(s.link));

  if (!isActive) {
    return null;
  }

  return (
    <div
      className={`${
        isSidebarOpen ? 'max-h-[1500px]' : 'max-h-0'
      } grid w-full overflow-hidden transition-all duration-300 ease-in-out`}
    >
      {wizardSteps.map((step, index) => {
        const parentIsActive = hasPartialMatch(step.link);

        return (
          <div key={step.link} className='gap-1 first:mt-5'>
            <EstimationNavigationItem
              index={index}
              key={step.label}
              label={step.label}
              navigateTo={step.link}
              disabled={!phaseResponses.length && index > indexOfPhases}
              isOmitted={step.isOmitted}
              ignoreNumbering={step.ignoreNumbering}
              stepOffset={stepOffset}
              parentIndexOffset={parentIndexOffset}
              isActive={parentIsActive && !step.isOmitted}
              hasChildren={Boolean(step.children)}
              onItemClick={onItemClick}
            />

            {parentIsActive &&
              step.children?.map((child, childIndex) => (
                <EstimationNavigationItem
                  key={child.label}
                  label={child.label}
                  index={childIndex}
                  navigateTo={child.link}
                  disabled={false}
                  isOmitted={child.isOmitted}
                  ignoreNumbering={child.ignoreNumbering}
                  stepOffset={stepOffset}
                  parentIndexOffset={parentIndexOffset}
                  hasChildren={false}
                  isChild={true}
                  parentIndex={index}
                  onItemClick={onItemClick}
                />
              ))}
          </div>
        );
      })}

      <Button
        className='btn btn-outline-secondary btn-sm dropdown-toggle border-none'
        variant='secondary'
        outlined
        onClick={() => setIsVersionModalOpen(true)}
      >
        {messages.currentVersion({
          versionName: savedEstimation?.estimation.versionName,
        })}
        <span>
          <IconCaretDown className='ml-2 inline-block ltr:ml-1 rtl:mr-1' />
        </span>
      </Button>
      <EstimationVersionModal
        open={isVersionModalOpen}
        onClose={() => setIsVersionModalOpen(false)}
        estimationId={savedEstimation?.estimation.id}
      />
    </div>
  );
};
